import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useNavigate } from '@tanstack/router'
import type { InputHTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useGeoCheck from '../../hooks/useGeoCheck'
import type { ApiResponse } from '../../types/api'
import type { ResetCredentials, UserInfo } from '../../types/user'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import Icon from '../icon'
import MessageModal from './message-modal'
import Modal from './modal'
import Spinner from './spinner'

const StyledInput = (props: InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className="flex-1 py-[7px] pr-[25px] mb-[10px] text-base text-txt rounded-none border-0 border-b-txt border-b border-solid placeholder-[#ccc] dark:placeholder-[rgba(255,255,255,0.6)] transition-all duration-200 ease-in-out"
  />
)

const PasswordResetModal = ({ code, email }: ResetCredentials) => {
  const modal = useModal()
  const messageModal = useModal(MessageModal)
  const { geoCheck } = useGeoCheck()
  const { t } = useTranslation([
    'common',
    'login',
    'recoverPassword',
    'register',
  ])
  const navigate = useNavigate({ from: '/' })
  const [formData, setFormData] = useState({
    code: '',
    newPassword: '',
    confirmNewPassword: '',
  })
  const [hide, setHide] = useState({
    code: true,
    newPassword: true,
    confirmNewPassword: true,
  })
  const [message, setMessage] = useState<{
    message: string
    isValid: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setFormData(prev => ({ ...prev, code }))
  }, [code])

  const onReset = async () => {
    const { newPassword, confirmNewPassword, code } = formData
    if (!newPassword)
      return setMessage({
        message: t('new-password-required', { ns: 'register' }),
        isValid: false,
      })
    if (!confirmNewPassword)
      return setMessage({
        message: t('confirm-new-password-required', { ns: 'register' }),
        isValid: false,
      })
    if (newPassword !== confirmNewPassword)
      return setMessage({
        message: t('confirm-password-doesnt-match', { ns: 'register' }),
        isValid: false,
      })

    setIsLoading(true)
    const reqBody = {
      type: 'reset_password',
      email,
      code,
      password: newPassword,
    }
    try {
      const response = await api.post<ApiResponse<UserInfo | string>>(
        '/request.php',
        reqBody,
      )
      const { info, status } = response.data
      if (status === 1) {
        navigate({ to: '/', search: undefined })
        closeModal(modal)
        messageModal.show({
          title: t('congratulations', { ns: 'common' }),
          message: t('password-changed', { ns: 'common' }),
        })
      } else {
        setMessage({ message: info as string, isValid: false })
      }
    } catch {
      setMessage({
        message: t('something-went-wrong', { ns: 'common' }),
        isValid: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const renderPasswordIcon = (
    field: 'code' | 'newPassword' | 'confirmNewPassword',
  ) => {
    return (
      <div
        onClick={() =>
          setHide(prev => ({ ...prev, [field]: prev[field] ? false : true }))
        }
        className="absolute flex top-[calc(50%-10px)] right-0 translate-y-[-50%] cursor-pointer stroke-txt">
        {hide[field] ? (
          <Icon id="eye-slash" width="20" height="20" />
        ) : (
          <Icon id="eye" width="20" height="20" />
        )}
      </div>
    )
  }

  return (
    <Modal id="password-reset-modal">
      {/* Title */}
      <p className="mb-[25px] text-[15px] text-txt text-center font-Syncopate-Bold uppercase">
        {t('change-password', { ns: 'recoverPassword' })}
      </p>
      {message && (
        <p
          className={classNames(
            'text-[15px] mb-[10px]',
            message.isValid ? 'text-success' : 'text-error',
          )}>
          {message.message}
        </p>
      )}
      <div className="flx-ctr relative">
        <StyledInput
          name="code"
          placeholder={t('temporary-password', { ns: 'recoverPassword' })}
          value={formData.code}
          readOnly
          type={hide.code ? 'password' : 'text'}
        />
        {renderPasswordIcon('code')}
      </div>
      <div className="flx-ctr relative">
        <StyledInput
          name="newPassword"
          placeholder={t('new-password', { ns: 'recoverPassword' })}
          value={formData.newPassword}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          type={hide.newPassword ? 'password' : 'text'}
        />
        {renderPasswordIcon('newPassword')}
      </div>
      <div className="flx-ctr relative">
        <StyledInput
          name="confirmNewPassword"
          placeholder={t('confirm-new-password', { ns: 'recoverPassword' })}
          value={formData.confirmNewPassword}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          type={hide.confirmNewPassword ? 'password' : 'text'}
        />
        {renderPasswordIcon('confirmNewPassword')}
      </div>
      <div className="flx-ctr mx-0 my-5">
        {isLoading ? (
          <Spinner />
        ) : (
          <button
            onClick={onReset}
            className="block py-[7px] px-6 mx-auto bg-primary-btn-bg border-[1.5px] border-primary-btn-bg rounded-[22px] cursor-pointer text-[15px] text-primary-btn-txt text-center font-normal transition-all duration-200 ease-in-out">
            {t('confirm', { ns: 'common' })}
          </button>
        )}
      </div>
      <p className="mb-[5px] text-[15px] text-txt text-center">
        {t('no-account', { ns: 'login' })}{' '}
        <button
          className="underline text-highlight"
          onClick={() => {
            geoCheck(() => navigate({ to: '/register' }))
            closeModal(modal)
          }}>
          {t('join-now', { ns: 'login' })}
        </button>
      </p>
    </Modal>
  )
}

export default NiceModal.create(PasswordResetModal)
